import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSoundPacks } from '../context/SoundPacksContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { signOutUser } from '../services/auth';
import { DatabaseUserModel, MAX_REGISTERED_DEVICES } from '../services/DatabaseUser';
import { DatabaseSettingsModel } from '../services/DatabaseSettings';
import { DatabaseSoundPackModel } from '../services/DatabaseSoundPack';
import { createStripePortalSession, listPackPurchases, PackPurchase } from '../services/stripe/stripeApi';
import Button from './ui/Button';
import LinkButton from './ui/LinkButton';
import { AppleIcon, WindowsIcon } from './ui/PlatformIcons';

// The Account page: one centred column capped at 620px, sections split by
// hairline rules rather than sitting in white cards. Purchase History and
// Registered Devices collapse behind their section label (chevron toggle,
// both closed by default); Purchase History fetches on its first expand.
// Everything else is a straight restyle of the earlier card layout.
//
// Type uses the `text-r-*` scale (flat px, line-height:normal, no baked
// tracking), not the older `fontSize` tokens which bake both. Tracking is added
// inline only where the design has it (-0.02em title, -0.01em "All-Access",
// 0.08em section labels). Colours: `ink-secondary` (#292927), `subtle` (#9A9A9A),
// `ghost-ink` (counts / "member since"), `danger` (sign out), `caution`
// (sub "cancels"); rule `border-placeholder` (#D8D8D8). All in tailwind.config.js.

// Formats a Stripe amount (in the currency's minor unit, e.g. cents) for display.
function formatAmount(amount: number | null, currency: string | null): string {
    if (amount === null || currency === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(amount / 100);
}

function formatLongDate(date: Date): string {
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Purchase line items carry only the pack name as it read at checkout (no
// image), so the cover is looked up from the sound-pack catalogue by name —
// exact match first, then a pack whose name prefixes the description.
function coverForDescription(description: string | null, byName: Map<string, string>): string | undefined {
    if (!description) return undefined;
    const d = description.trim().toLowerCase();
    if (byName.has(d)) return byName.get(d);
    for (const [name, url] of Array.from(byName)) if (d.startsWith(name)) return url;
    return undefined;
}

function UserProfile() {
    const { user, userData, isAdmin: isAdminUser } = useUser();
    const { soundPacks } = useSoundPacks();
    const { settings } = useAppSettings();
    const navigate = useNavigate();

    // All-Access prices — always the live Firestore values (settings/settings),
    // shared with the Subscribe page and the cart banner. The literals are only
    // first-paint / offline fallbacks. The upsell advertises the cheapest rate
    // (yearly, per month); a subscriber sees the price of the plan they're on.
    const fromPriceLabel = DatabaseSoundPackModel.formatPrice(
        DatabaseSettingsModel.yearlyMonthlyPrice(settings) ?? '7.99',
    );
    const subscribedPriceLabel = userData?.subscription?.interval === 'year'
        ? `${DatabaseSoundPackModel.formatPrice(settings?.yearlySubscriptionPrice ?? '95.88')}/year`
        : `${DatabaseSoundPackModel.formatPrice(settings?.subscriptionPrice ?? '9.99')}/month`;

    const coverByPackName = useMemo(() => {
        const map = new Map<string, string>();
        for (const pack of soundPacks) {
            if (pack.coverImageUrl) map.set(pack.name.trim().toLowerCase(), pack.coverImageUrl);
        }
        return map;
    }, [soundPacks]);
    const [isResetting, setIsResetting] = useState(false);
    const [isOpeningPortal, setIsOpeningPortal] = useState(false);
    const [purchases, setPurchases] = useState<PackPurchase[] | null>(null);
    const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
    const [purchasesError, setPurchasesError] = useState<string | null>(null);
    const [pluginVersion, setPluginVersion] = useState<string | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [devicesOpen, setDevicesOpen] = useState(false);

    useEffect(() => {
        DatabaseSettingsModel.getCurrentVersion()
            .then(setPluginVersion)
            .catch((error) => console.error('Error loading plugin version:', error));
    }, []);

    const handleSignOut = async () => {
        try {
            await signOutUser();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleManageSubscription = async () => {
        try {
            setIsOpeningPortal(true);
            const url = await createStripePortalSession();
            window.location.href = url;
        } catch (error) {
            console.error('Error opening subscription portal:', error);
            alert('Failed to open subscription portal. Please try again.');
            setIsOpeningPortal(false);
        }
    };

    const loadPurchases = async () => {
        try {
            setIsLoadingPurchases(true);
            setPurchasesError(null);
            const result = await listPackPurchases();
            setPurchases(result);
        } catch (error) {
            console.error('Error loading purchase history:', error);
            setPurchasesError('Failed to load purchase history. Please try again.');
        } finally {
            setIsLoadingPurchases(false);
        }
    };

    // Fetch on the first expand (and again after an error, since `purchases`
    // stays null until a load succeeds).
    const toggleHistory = () => {
        const next = !historyOpen;
        setHistoryOpen(next);
        if (next && purchases === null && !isLoadingPurchases) void loadPurchases();
    };

    const handleResetPurchases = async () => {
        if (!user) return;

        const confirmed = window.confirm(
            'Are you sure you want to reset all sound pack purchases? This will remove all owned packs from your account.',
        );
        if (!confirmed) return;

        try {
            setIsResetting(true);
            await DatabaseUserModel.resetOwnedSoundPacks(user.uid);
            alert('Sound pack purchases have been reset successfully!');
        } catch (error) {
            console.error('Error resetting purchases:', error);
            alert('Failed to reset purchases. Please try again.');
        } finally {
            setIsResetting(false);
        }
    };

    if (!user) {
        return <p className="text-r-body font-semibold text-subtle">No user logged in.</p>;
    }

    const subscription = userData?.subscription ?? null;
    const isSubscribed = subscription?.status === 'active';
    const deviceEntries = Object.entries(userData?.registeredDevices ?? {});
    const createdDate = userData?.createdAt?.toDate?.();
    const memberSince = createdDate instanceof Date ? formatLongDate(createdDate) : 'Unknown';

    return (
        <div className="mx-auto flex max-w-[620px] flex-col text-ink-secondary">
            <header className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
                <h1 className="text-r-title font-extrabold tracking-[-0.02em]">Account</h1>
                <div className="flex flex-col gap-0.5 sm:items-end">
                    <span className="text-r-sub text-subtle">{user.email}</span>
                    <span className="text-r-cap text-ghost-ink">Member since {memberSince}</span>
                </div>
            </header>

            <Section className="mt-6">
                <SectionHeading>Subscription</SectionHeading>
                {isSubscribed && subscription ? (
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex flex-col gap-1">
                            <span className="text-r-lead font-extrabold tracking-[-0.01em]">All-Access</span>
                            <span
                                className={`text-r-sub ${
                                    subscription.cancelAtPeriodEnd ? 'text-caution' : 'text-subtle'
                                }`}
                            >
                                {subscription.currentPeriodEnd
                                    ? `${subscription.cancelAtPeriodEnd ? 'Cancels' : 'Renews'} on ${formatLongDate(
                                          new Date(subscription.currentPeriodEnd * 1000),
                                      )}`
                                    : 'Active'}
                                {!subscription.cancelAtPeriodEnd && ` · ${subscribedPriceLabel}`}
                            </span>
                        </div>
                        <Button
                            variant="secondary"
                            size="card"
                            onClick={handleManageSubscription}
                            disabled={isOpeningPortal}
                        >
                            {isOpeningPortal ? 'Opening…' : 'Manage Subscription'}
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <span className="text-r-body text-subtle">You are not subscribed.</span>
                        <Button variant="primary" size="card" onClick={() => navigate('/subscribe')}>
                            Get All-Access
                            <span className="ml-2 font-semibold text-white/60">
                                from {fromPriceLabel}/mo
                            </span>
                        </Button>
                    </div>
                )}
            </Section>

            <Section>
                <SectionHeading version={pluginVersion ? `v${pluginVersion}` : undefined}>
                    Download Plugin
                </SectionHeading>
                {pluginVersion ? (
                    <div className="flex flex-wrap gap-2.5">
                        <Button
                            variant="secondary"
                            size="card"
                            onClick={() => navigate('/download-started?platform=mac')}
                            className="flex-1 basis-[180px] gap-2"
                        >
                            <AppleIcon />
                            macOS
                        </Button>
                        <Button
                            variant="secondary"
                            size="card"
                            onClick={() => navigate('/download-started?platform=windows')}
                            className="flex-1 basis-[180px] gap-2"
                        >
                            <WindowsIcon />
                            Windows
                        </Button>
                    </div>
                ) : (
                    <p className="text-r-body text-subtle">Loading…</p>
                )}
            </Section>

            <CollapsibleSection
                label="Purchase History"
                count={purchases?.length}
                open={historyOpen}
                onToggle={toggleHistory}
            >
                {isLoadingPurchases ? (
                    <p className="text-r-body text-subtle">Loading…</p>
                ) : purchasesError ? (
                    <p className="text-r-cap font-medium text-alert">{purchasesError}</p>
                ) : purchases && purchases.length === 0 ? (
                    <p className="text-r-body text-subtle">No pack purchases yet.</p>
                ) : purchases ? (
                    <div className="flex flex-col gap-4">
                        {purchases.flatMap((purchase) =>
                            purchase.items.map((item, ii) => (
                                <div key={`${purchase.id}-${ii}`} className="flex items-center gap-3.5">
                                    <PurchaseCover src={coverForDescription(item.description, coverByPackName)} />
                                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        <span className="text-r-item font-bold">
                                            {item.description || 'Sound pack'}
                                            {item.quantity && item.quantity > 1 ? ` × ${item.quantity}` : ''}
                                        </span>
                                        <span className="text-r-cap text-subtle">
                                            {formatLongDate(new Date(purchase.created * 1000))}
                                        </span>
                                    </div>
                                    <span className="text-r-body font-bold">
                                        {formatAmount(item.amount, purchase.currency)}
                                    </span>
                                </div>
                            )),
                        )}
                    </div>
                ) : null}
            </CollapsibleSection>

            <CollapsibleSection
                label="Registered Devices"
                count={`${deviceEntries.length} of ${MAX_REGISTERED_DEVICES}`}
                open={devicesOpen}
                onToggle={() => setDevicesOpen((v) => !v)}
            >
                {deviceEntries.length === 0 ? (
                    <p className="text-r-body text-subtle">No devices registered yet.</p>
                ) : (
                    <div className="flex flex-col gap-2.5">
                        {deviceEntries.map(([deviceId, name]) => (
                            <span key={deviceId} className="text-r-body">
                                {name}
                            </span>
                        ))}
                    </div>
                )}
            </CollapsibleSection>

            {isAdminUser && (
                <Section>
                    <SectionHeading>Admin</SectionHeading>
                    <div className="flex flex-col items-start gap-3">
                        <LinkButton tone="secondary" onClick={() => navigate('/admin')}>
                            Go to Admin Panel
                        </LinkButton>
                        <LinkButton tone="alert" onClick={handleResetPurchases} disabled={isResetting}>
                            {isResetting ? 'Resetting…' : 'Reset Sound Pack Purchases'}
                        </LinkButton>
                    </div>
                </Section>
            )}

            <div className="flex justify-end border-t border-placeholder pt-6">
                <button
                    onClick={handleSignOut}
                    className="text-r-body font-bold text-danger transition-colors hover:text-danger-hover"
                >
                    Sign out
                </button>
            </div>
        </div>
    );
}

export default UserProfile;

// ============================ Section primitives ============================

// A page section: hairline top rule (#D8D8D8), then a stacked label + body.
function Section({ className = '', children }: { className?: string; children: React.ReactNode }) {
    return (
        <section className={`flex flex-col gap-4 border-t border-placeholder pb-7 pt-6 ${className}`}>{children}</section>
    );
}

// The uppercase micro-label at the top of a section: its text plus, optionally,
// a ghosted `[…]` count inline (purchase / device counts).
function SectionLabel({ count, children }: { count?: React.ReactNode; children: React.ReactNode }) {
    return (
        <h2 className="text-r-cap font-bold uppercase tracking-[0.08em] text-subtle">
            {children}
            {count !== undefined && <span className="ml-1.5 text-ghost-ink">[{count}]</span>}
        </h2>
    );
}

// A static section label. `version` sits to the right of it in plain mid-grey
// (plugin version).
function SectionHeading({
    count,
    version,
    children,
}: {
    count?: React.ReactNode;
    version?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex items-baseline gap-2.5">
            <SectionLabel count={count}>{children}</SectionLabel>
            {version && <span className="text-r-cap text-subtle">{version}</span>}
        </div>
    );
}

// A section whose body collapses behind its label. The whole label row is the
// toggle; a chevron on the right flips when open.
function CollapsibleSection({
    label,
    count,
    open,
    onToggle,
    children,
}: {
    label: string;
    count?: React.ReactNode;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    return (
        <Section>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="flex items-center justify-between gap-3 text-left text-subtle"
            >
                <SectionLabel count={count}>{label}</SectionLabel>
                <svg
                    width="11"
                    height="7"
                    viewBox="0 0 11 7"
                    aria-hidden="true"
                    className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                >
                    <path
                        d="M1 1L5.5 5.5L10 1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
            {open && children}
        </Section>
    );
}

// 48px rounded artwork for a purchase row. Falls back to a bare bordered tile
// when the pack (and so its cover) can't be resolved from the description.
function PurchaseCover({ src }: { src?: string }) {
    return (
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-black/[0.06] bg-surface-muted">
            {src && <img src={src} alt="" className="h-full w-full object-cover" />}
        </div>
    );
}

